// ============================================================
// E2：大屏 AI 操作计划 → 命令总线命令映射
// 应用走命令总线（dashboard 作用域；手势批次 = 1 个撤销步）
// ============================================================
import type { VisualComponent } from '@dtwin/shared';
import type { CommandSpec } from '../core/commandBus';
import type { DashboardOp } from '../api/aiEditor';

export interface DashboardOpCommandBuild {
  commands: CommandSpec[];
  descriptions: string[];
}

type Comp = VisualComponent;

export function dashboardOpsToCommands(
  ops: DashboardOp[],
  components: Comp[],
  pageConfig?: { width?: number; height?: number },
): DashboardOpCommandBuild {
  const byId = new Map(components.map((c) => [c.id, c]));
  const commands: CommandSpec[] = [];
  const descriptions: string[] = [];
  const width = pageConfig?.width ?? 1920;
  const height = pageConfig?.height ?? 1080;
  const center = (size?: { width: number; height: number }) => ({
    x: Math.max(0, Math.round((width - (size?.width ?? 300)) / 2)),
    y: Math.max(0, Math.round((height - (size?.height ?? 200)) / 2)),
  });

  for (const op of ops) {
    switch (op.op) {
      case 'dashboard.theme':
        commands.push({ type: 'page.config.patch', payload: { patch: op.pageConfig } });
        descriptions.push(`切换主题「${op.themeName}」${op.reason ? `（${op.reason}）` : ''}`);
        break;
      case 'widget.add':
        commands.push({
          type: 'widget.add',
          payload: {
            component: {
              type: op.component.type,
              position: center(op.component.size),
              ...(op.component.size ? { size: op.component.size } : {}),
              ...(op.component.base ? { base: op.component.base } : {}),
              ...(op.component.special ? { special: op.component.special } : {}),
              ...(op.component.style ? { style: op.component.style } : {}),
              ...(op.component.config ? { config: op.component.config } : {}),
            },
          },
        });
        descriptions.push(`添加组件「${(op.component.base?.title as string) ?? op.component.type}」`);
        break;
      case 'widget.replace': {
        const existing = byId.get(op.componentId);
        if (!existing) break;
        commands.push({ type: 'widget.remove', payload: { id: op.componentId } });
        commands.push({
          type: 'widget.add',
          payload: {
            component: {
              type: op.component.type,
              position: { ...existing.position },
              size: { ...existing.size },
              ...(op.component.base ? { base: { ...op.component.base } } : {}),
              ...(op.component.special ? { special: op.component.special } : {}),
              ...(op.component.style ? { style: op.component.style } : {}),
              ...(op.component.config ? { config: op.component.config } : {}),
            },
          },
        });
        descriptions.push(`替换「${(existing.base?.title as string) ?? existing.type}」为「${op.component.type}」`);
        break;
      }
      case 'widget.update': {
        const existing = byId.get(op.componentId);
        if (!existing) break;
        const patch: Record<string, unknown> = {};
        if (op.patch.base && typeof op.patch.base === 'object') patch.base = { ...existing.base, ...(op.patch.base as Record<string, unknown>) };
        if (op.patch.special && typeof op.patch.special === 'object') patch.special = { ...existing.special, ...(op.patch.special as Record<string, unknown>) };
        if (op.patch.style && typeof op.patch.style === 'object') patch.style = { ...existing.style, ...(op.patch.style as Record<string, unknown>) };
        if (op.patch.config && typeof op.patch.config === 'object') patch.config = { ...existing.config, ...(op.patch.config as Record<string, unknown>) };
        if (Object.keys(patch).length === 0) break;
        commands.push({ type: 'widget.update', payload: { id: op.componentId, patch } });
        descriptions.push(`修改「${(existing.base?.title as string) ?? existing.type}」`);
        break;
      }
      case 'widget.bind': {
        const existing = byId.get(op.componentId);
        if (!existing) break;
        const ds = (existing.dataSource ?? { type: 'device', config: {} }) as {
          type: string;
          config?: Record<string, unknown>;
        };
        commands.push({
          type: 'widget.update',
          payload: {
            id: op.componentId,
            patch: {
              dataSource: {
                ...ds,
                ...(op.dataSetId ? { dataSetId: op.dataSetId } : {}),
                config: {
                  ...(ds.config ?? {}),
                  ...(op.deviceId ? { deviceId: op.deviceId } : {}),
                  ...(op.pointKey ? { pointKey: op.pointKey } : {}),
                },
              },
            },
          },
        });
        descriptions.push(
          `绑定「${(existing.base?.title as string) ?? existing.type}」→ ${op.dataSetId ?? op.deviceId ?? op.pointKey}`,
        );
        break;
      }
      case 'widget.remove': {
        const existing = byId.get(op.componentId);
        if (!existing) break;
        commands.push({ type: 'widget.remove', payload: { id: op.componentId } });
        descriptions.push(`删除「${(existing.base?.title as string) ?? existing.type}」`);
        break;
      }
      default:
        break;
    }
  }
  return { commands, descriptions };
}
